import { Link } from 'react-router-dom';
import { Loader2, ExternalLink, Calendar, Euro, FolderOpen } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useOpportunities } from '../../hooks/useOpportunities';
import { STATUS_CONFIG } from '../../types/internal-opportunity.types';

interface CompanyOpportunitiesTabProps {
  companyId: number;
}

function formatAmount(value: number | undefined, currency = 'EUR'): string {
  if (!value) return '—';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(value: string | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('fr-FR');
}

export function CompanyOpportunitiesTab({ companyId }: CompanyOpportunitiesTabProps) {
  const { data, isLoading, error } = useOpportunities(
    { buyerId: companyId },
    { page: 1, pageSize: 50, sortBy: 'createdAt', sortOrder: 'desc' }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-copper" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-destructive/10 text-destructive rounded-lg">
        Erreur lors du chargement des opportunités
      </div>
    );
  }

  const opportunities = data?.data ?? [];

  if (opportunities.length === 0) {
    return (
      <div className="border rounded-lg py-12 text-center text-muted-foreground">
        <FolderOpen className="h-10 w-10 mx-auto mb-3 text-muted-foreground/60" />
        <p>Aucune opportunité associée à cette société</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {data?.meta.total} opportunité{(data?.meta.total ?? 0) > 1 ? 's' : ''}
      </p>
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Titre</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>
                <Calendar className="h-3.5 w-3.5 inline mr-1" />
                Date limite
              </TableHead>
              <TableHead>
                <Euro className="h-3.5 w-3.5 inline mr-1" />
                Montant estimé
              </TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {opportunities.map((opp) => {
              const statusCfg = STATUS_CONFIG[opp.status];
              return (
                <TableRow key={opp.id} className="hover:bg-muted/30">
                  <TableCell>
                    <Link
                      to={`/tm/opportunities/${opp.id}`}
                      className="font-medium hover:text-copper"
                    >
                      {opp.title}
                    </Link>
                    {opp.reference && (
                      <div className="text-xs text-muted-foreground">
                        {opp.reference}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      className={`${statusCfg.bgColor} ${statusCfg.color}`}
                    >
                      {statusCfg.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatDate(opp.submissionDeadline)}
                  </TableCell>
                  <TableCell className="text-sm font-medium">
                    {formatAmount(opp.estimatedAmount, opp.currency)}
                  </TableCell>
                  <TableCell>
                    <Link
                      to={`/tm/opportunities/${opp.id}`}
                      className="text-muted-foreground hover:text-copper"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
